import React, { useEffect, useState } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import AdminDataTable from "../../components/common/AdminDataTable";
import { buklEmailColumns } from "../../tables/buklEmailColumns";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { can } from "../../utils/can";

export default function BulkEmail() {
  const PAGE_SIZE = 10;

  const [history, setHistory] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [message, setMessage] = useState("");
  
  const [form, setForm] = useState({
    sendTo: "",
    content: "",
  });

  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});

  const token = localStorage.getItem("adminToken");
  const API_URL = process.env.REACT_APP_BASE_URL_API;

  /* ================= VALIDATION ================= */
  const validate = () => {
    const err = {};
    if (!form.sendTo) err.sendTo = "Required";
    if (!form.content || form.content.length < 10)
      err.content = "Minimum 10 characters required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ================= FETCH LIST ================= */
  const fetchEmail = async (pageNo = 1) => {
    try {
      setListLoading(true);

      const res = await fetch(
        `${API_URL}/api/admin/bulk/email-list?page=${pageNo}&limit=${PAGE_SIZE}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const json = await res.json();

      setHistory(json.data || []);
      setTotalPages(json.totalPages || 1);
      setPage(pageNo);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchEmail(page);
  }, []);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if(can("bulk_email", "create") === false){
      alert("You don't have permission to perform this action.");
      return;
    }

    if (!validate()) return;

    try {
      const url = editId
        ? `${API_URL}/api/admin/bulk/email-list/update/${editId}`
        : `${API_URL}/api/admin/bulk/email-create`;

      const method = "POST";

     const response = await fetch(url, {
                  method,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(form),
                });

        const result = await response.json();

      if(response.ok){
        resetForm();
        fetchEmail(page);
        setMessage(result.message);
      }else{
        setMessage(result.message);
      }
      
    } catch (err) {
      console.error("Submit error", err);
    }
  };

  /* ================= EDIT ================= */
  const handleEdit = (row) => {
    if(can("bulk_email", "edit") === false){
      alert("You don't have permission to perform this action.");
      return;
    }

    setEditId(row._id);
    setForm({
      sendTo: row.sendTo,
      content: row.content,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if(can("bulk_email", "delete") === false){
      alert("You don't have permission to perform this action.");
      return;
    }

    if (!window.confirm("Delete this record?")) return;

    try {
      await fetch(`${API_URL}/api/admin/bulk/email-list/delete/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      fetchEmail(page);
    } catch (err) {
      console.error("Delete error", err);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setForm({ sendTo: "", content: "" });
    setErrors({});
  };

  const columns = buklEmailColumns(handleEdit, handleDelete, PAGE_SIZE);

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Bulk Email" />

        <div className="right_col" role="main">
          

          {/* ================= FORM ================= */}
          <div className="x_panel">
            <h2>{editId ? "Edit Bulk Email" : "Bulk Email Form"}</h2>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label>Send To</label>
                <select
                  className="form-select"
                  value={form.sendTo}
                  onChange={(e) =>
                    setForm({ ...form, sendTo: e.target.value })
                  }
                >
                  <option value="">Select</option>
                  <option value="All Members">All Members</option>
                  <option value="Paid Members">Paid Members</option>
                  <option value="Unpaid Members">Unpaid Members</option>
                </select>
                {errors.sendTo && (
                  <p className="text-danger">{errors.sendTo}</p>
                )}
              </div>
              

              <div className="mb-3">
                <label>Message</label>

                <CKEditor
                  editor={ClassicEditor}
                  data={form.content}
                  onChange={(event, editor) => {
                    const data = editor.getData();

                    setForm((prev) => ({
                      ...prev,
                      content: data,
                    }));

                    // live validation clear
                    if (data && data.length >= 10) {
                      setErrors((prev) => ({ ...prev, content: "" }));
                    }
                  }}
                  onBlur={() => {
                    if (!form.content || form.content.length < 10) {
                      setErrors((prev) => ({
                        ...prev,
                        content: "Minimum 10 characters required",
                      }));
                    }
                  }}
                />

                {errors.content && (
                  <p className="text-danger">{errors.content}</p>
                )}
                </div>


              <button className="btn sitebtn">
                {editId ? "Update" : "Submit"}
              </button>

             <p className="text-success">{message}</p>
            </form>
          </div>

          {/* ================= LIST ================= */}
          <div className="x_panel">
            <h2>Bulk Email List</h2>

            <AdminDataTable
              columns={columns}
              data={history}
              loading={listLoading}
              page={page}
              totalPages={totalPages}
              onPageChange={(p) => fetchEmail(p)}
            />
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
